import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WebSocketNotificationTypeEnum } from './web-socket-notification-type.enum';

export interface WebSocketEventsHandler {
  onWsOpen(): void;
  onWsError(): void;
  onWsClose(): void;
  onWsSuccess(): void;
  onWsEvent(message: string): void;
}

export class ReconnectingWebSocket {
  message$ = new Subject<any>();

  private delayS = 16;
  private maxDelayS = 128;
  private webSocket: WebSocket = null;
  private timeout = null;
  private closing = false;
  private receivedAtLeastOneMessage = false;

  constructor(private http: HttpClient, private companyId: string | number, private tracker: WebSocketEventsHandler) {}

  open() {
    const subscriptionUrl = `${environment.apiURLPrefix}/${this.companyId}/subscriptions`;
    this.http.post<any>(subscriptionUrl, null).subscribe({
      next: ({ ws_jwt_token }: { ws_jwt_token: string }) => this.connectWebSocket(ws_jwt_token),
      error: () => this.restart(),
    });
  }

  close() {
    this.closing = true;
    this.message$.complete();
    if (this.webSocket) {
      this.webSocket.onerror = null;
      this.webSocket.onclose = null;
      this.webSocket.onmessage = null;
      this.webSocket.close();
    }
  }

  private restart() {
    this.receivedAtLeastOneMessage = false;
    if (this.timeout === null) {
      const delayMs = Math.random() * this.delayS * 1000;
      this.timeout = setTimeout(() => {
        if (!this.closing) {
          this.open();
          this.timeout = null;
        }
      }, delayMs);
      this.delayS = Math.min(2 * this.delayS, this.maxDelayS);
    }
  }

  private onWebSocketError() {
    this.tracker.onWsError();
    this.restart();
  }

  private onWebSocketClose() {
    this.tracker.onWsClose();
    this.restart();
  }

  private onWebSocketMessage(message: MessageEvent) {
    // We should reset delayS = 2 in onopen but onopen is invoked
    // just before onerror by some browsers (such as chrome)
    // so we can't rely on onopen as a meaningful signal of "ok".
    // Instead, we consider we need to get at least one event on
    // a websocket to be good.
    if (!this.receivedAtLeastOneMessage) {
      this.receivedAtLeastOneMessage = true;
      this.tracker.onWsSuccess();
      this.delayS = 2;
    }
    const event = JSON.parse(message.data);
    this.message$.next(event.data);
    this.tracker.onWsEvent(this.compactWSPayload(event.data));
  }

  private connectWebSocket(ws_jwt_token: string) {
    const fullWsUrl = `${environment.wsUrl}?ws_jwt_token=${ws_jwt_token}`;
    this.tracker.onWsOpen();
    this.webSocket = new WebSocket(fullWsUrl);
    // It is not really possible to distinguish between normal and erronous closes
    // because different browsers report different events (onclose and/or onerror)
    // so we handle both events just the same.
    this.webSocket.onerror = () => this.onWebSocketError();
    this.webSocket.onclose = () => this.onWebSocketClose();
    this.webSocket.onmessage = message => this.onWebSocketMessage(message);
  }

  // there are 350+ million such events per week, it makes sense to optimize how we store them
  private compactWSPayload(notif) {
    try {
      switch (notif.type) {
        case WebSocketNotificationTypeEnum.Action:
          return `A${notif.action_id}`;
        case WebSocketNotificationTypeEnum.Task:
          return `T${notif.task_id}${notif.task_state}`;
        case WebSocketNotificationTypeEnum.AsyncRequest:
          return `AR${notif.data.id}`;
        default:
          return notif.type;
      }
    } catch (e) {
      return 'X';
    }
  }
}
